import React from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { shape, arrayOf, string, number, oneOfType, object } from 'prop-types'
import { FormattedMessage } from 'react-intl'
import { Text, If } from 'src/Components'
import classnames from 'classnames'
import { IconTilde, IconHome } from 'src/Components/shared/Icon'

import Carousel from 'src/Components/shared/Carousel'
import {
  selectClientFetchId,
  selectYlopoOptIn
} from 'src/store/selectors/client'
import { selectAuthedAsClient } from 'src/store/selectors/auth'
import { toggleYlopoOptInModal } from 'src/actions/ylopo'
import { buildListingUrl } from 'src/helpers/ylopo'
import Format from 'src/Components/shared/Format'

import styles from './YlopoListings.module.scss'

const generateListingSlides = (
  listings,
  lastSlide,
  clientId,
  clientHasOptedIn,
  dispatch
) => {
  if (lastSlide) {
    listings = [...listings, lastSlide]
  }

  const handleNavigate = listing => {
    return () => {
      const listingUrl = buildListingUrl(clientId, listing)

      handleOptInClick(listingUrl)
    }
  }

  const handleOptInClick = url => {
    if (clientHasOptedIn) {
      window.open(url)
    } else {
      dispatch(toggleYlopoOptInModal(url))
    }
  }

  return listings.map(listing => {
    if (listing.id === 'lastSlide') {
      return (
        <button
          key={listing.id}
          onClick={() => handleOptInClick(listing.url)}
          className={styles.lastListingWrapper}
        >
          <IconHome className={styles.homeIcon} />
          <Text className={styles.lastSlideText}>
            <FormattedMessage
              id='MarketSpotLight.ylopoListings.see.more.slide'
              defaultMessage='Want to see even more homes?'
            />
          </Text>
          <Text className={styles.viewMoreText} size='s'>
            <FormattedMessage
              id='MarketSpotLight.ylopoListings.see.all.button'
              defaultMessage='View more'
            />
          </Text>
        </button>
      )
    }

    return (
      <button
        key={listing.id}
        onClick={handleNavigate(listing)}
        className={styles.listingImageContainer}
        style={{ backgroundImage: `url(${listing.mainPhoto})` }}
      >
        <div className={styles.listingContainer}>
          <div className={styles.listingInfo}>
            <div className={styles.listingBedBaths}>
              {listing.bedrooms} Beds &bull; {listing.bathrooms} Baths
            </div>
            <div className={styles.listingPrice}>{listing.formattedPrice}</div>
            {!!listing.pocketAmount && !!listing.mortgagePaymentDifference && (
              <div>
                <If condition={listing.mortgagePaymentDifference < 0}>
                  <Text className={styles.pocketAmountPerMonthText} size='xs'>
                    <FormattedMessage
                      id='MarketSpotLight.equitySmartSearch.save'
                      defaultMessage='Save'
                    />
                    <span className={styles.pocketAmountPerMonthSaved}>
                      <span
                        className={styles.paymentTilde}
                        aria-hidden='true'
                        title='about'
                      >
                        <IconTilde />
                      </span>
                      <span className={styles.srOnly}>about</span>
                      <Format.Currency
                        value={Math.abs(listing.mortgagePaymentDifference)}
                        abbreviate
                      />
                      /mo
                    </span>
                  </Text>
                </If>
                <If condition={listing.mortgagePaymentDifference >= 0}>
                  <Text
                    className={styles.pocketAmountPerMonthText}
                    size='xs'
                    color='heather'
                  >
                    <FormattedMessage
                      id='MarketSpotLight.equitySmartSearch.extra'
                      defaultMessage='Extra'
                    />
                    <span className={styles.pocketAmountPerMonthExtra}>
                      <span
                        className={styles.paymentTilde}
                        aria-hidden='true'
                        title='about'
                      >
                        <IconTilde />
                      </span>
                      <span className={styles.srOnly}>about</span>
                      <Format.Currency
                        value={Math.abs(listing.mortgagePaymentDifference)}
                        abbreviate
                      />
                      /mo
                    </span>
                  </Text>
                </If>
                <div className={styles.pocketAmountBox}>
                  <Text
                    className={styles.pocketAmountText}
                    size='xxs'
                    color='green'
                    element='div'
                  >
                    <FormattedMessage
                      id='MarketSpotLight.equitySmartSearch.pocket'
                      defaultMessage='+POCKET'
                    />
                  </Text>
                  <Text className={styles.pocketAmount} size='xl' color='green'>
                    <span
                      className={styles.pocketTilde}
                      aria-hidden='true'
                      title='about'
                    >
                      <IconTilde />
                    </span>
                    <span className={styles.srOnly}>about</span>
                    <Format.Currency value={listing.pocketAmount} abbreviate />
                  </Text>
                </div>
              </div>
            )}
          </div>
        </div>
      </button>
    )
  })
}

const YlopoListings = ({ listings, lastSlide }) => {
  const dispatch = useDispatch()
  const isClient = useSelector(selectAuthedAsClient)
  const clientId = useSelector(selectClientFetchId)
  const clientYlopoOptIn = useSelector(selectYlopoOptIn)
  const clientHasOptedIn = clientYlopoOptIn !== undefined || !isClient
  const slides = generateListingSlides(
    listings,
    lastSlide,
    clientId,
    clientHasOptedIn,
    dispatch
  )

  return (
    <Carousel
      className={classnames(styles.listingCarousel)}
      slides={slides}
      arrows
      infinite
      speed={300}
      swipeToSlide
      slidesToScroll={3}
      responsive={[
        {
          breakpoint: 420,
          settings: {
            slidesToShow: 2,
            rows: 1,
            slidesToScroll: 1
          }
        }
      ]}
    />
  )
}

YlopoListings.propTypes = {
  listings: arrayOf(
    shape({
      id: oneOfType([number, string]),
      mainPhoto: string,
      bedrooms: number,
      bathrooms: number,
      formattedPrice: string
    })
  ),
  lastSlide: object
}

export default YlopoListings
